import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey, type ApiClientError } from '@/lib/http-core';
import { errorFromResponse, friendlyMessage } from '@/lib/errors';

/*
 * 현금영수증 관리 — 고도몰 "현금영수증 발급/조회"·"현금영수증 개별발급" 두 화면을 옮긴다(QA [로직 검토] 현금영수증 발행 구현 여부).
 * 주문 단위 조회·발급·취소는 이미 있다(order PR #24, 주문 상세 카드 — features/orders/hooks/useOrderCashReceipt).
 * 이 화면은 그 위에 전 주문 목록과 주문서 없는 개별발급을 얹는다.
 *
 * BE 제안 계약(docs/handoff/BO-TO-BE-2026-10-13-cash-receipt-list.md) — 게이트웨이 `/admin/v1/orders/**` 경유:
 *   GET  /admin/v1/orders/cash-receipts                 목록(검색·페이지, meta.pagination.totalItems)
 *   GET  /admin/v1/orders/cash-receipts/order-check     개별발급 주문번호 유효성 검사(?orderNo=)
 *   POST /admin/v1/orders/cash-receipts                 개별발급(주문번호 없이도)
 *   POST /admin/v1/orders/cash-receipts/{id}/cancel     주문 없는 개별발급분 취소
 * 주문이 있는 행의 발급·재발급·취소는 기존 주문 단위 API(/orders/{orderId}/cash-receipt)를 그대로 쓴다.
 * BE 배포 전(404)이면 화면은 "서버 반영 후" 안내만 보인다.
 */

export const CASH_RECEIPT_READ_ROLES = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'] as const;
export const CASH_RECEIPT_WRITE_ROLES = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'] as const;

const BASE = '/admin/v1/orders/cash-receipts';
const orderPath = (orderId: string) => `/admin/v1/orders/${encodeURIComponent(orderId)}/cash-receipt`;

// ── 타입 ─────────────────────────────────────────────────────────────

/** 검색어 항목 — 고도몰 "통합검색" 드롭다운. ISSUE_NUMBER = 거래번호(국세청 승인번호). */
export type CashReceiptKeywordType = 'ALL' | 'ORDER_NO' | 'REQUESTER' | 'ISSUE_NUMBER';
/** 기간 기준 — 신청일 / 처리일(발급·취소·실패 시각). */
export type CashReceiptDateType = 'REQUESTED' | 'PROCESSED';
/** 발행상태 필터 — 주문 단위 응답의 issueStatus 와 같은 값. */
export type CashReceiptStatusFilter = 'ALL' | 'WAITING_DEPOSIT' | 'IN_PROGRESS' | 'ISSUED' | 'CANCELED' | 'FAILED';
export type CashReceiptTaxType = 'TAXABLE' | 'FREE' | 'MIXED';

export interface CashReceiptSearch {
  keywordType: CashReceiptKeywordType;
  keyword: string;
  dateType: CashReceiptDateType;
  from: string;
  to: string;
  orderStatuses: string[];
  issueStatus: CashReceiptStatusFilter;
  receiptType: 'ALL' | 'INCOME_DEDUCTION' | 'EXPENSE_PROOF';
  identityType: 'ALL' | 'PHONE' | 'BUSINESS_NO';
  taxType: 'ALL' | CashReceiptTaxType;
  amountChangedOnly: boolean;
  minAmount: string;
  maxAmount: string;
}

export type CashReceiptSort = 'REQUESTED_DESC' | 'REQUESTED_ASC' | 'ORDER_NO_DESC' | 'ORDER_NO_ASC';

export interface CashReceiptRow {
  /** 행 식별자 — 주문 신청이면 신청 id, 개별발급이면 발급 id. */
  id: string;
  /** 개별발급(주문서 없음)이면 null. */
  orderId: string | null;
  orderNo: string | null;
  requesterName: string;
  goodsName: string | null;
  receiptType: string;
  identityType: string;
  identityMasked: string | null;
  taxType: string;
  amount: number;
  supplyAmount: number;
  vatAmount: number;
  taxFreeAmount: number;
  /** 주문의 현재 결제금액 — 신청 후 금액이 바뀌었는지 비교(고도몰 "금액변경 건"). */
  orderAmount: number | null;
  amountChanged: boolean;
  paymentMethod: string | null;
  orderStatus: string | null;
  issueStatus: string;
  /** CHECKOUT · MYPAGE · ADMIN(주문 신청 경로) · MANUAL(개별발급) */
  requestSource: string | null;
  /** API(쇼핑몰 발급) · PG_WINDOW(결제창 자동) · LEGACY(고도몰 이관) — 발급분이 없으면 null. */
  receiptSource: string | null;
  issueNumber: string | null;
  receiptUrl: string | null;
  failureMessage: string | null;
  remainingAmount: number;
  requestedAt: string | null;
  processedAt: string | null;
}

export interface CashReceiptPage {
  items: CashReceiptRow[];
  total: number;
}

export interface OrderCheckResult {
  orderId: string;
  orderNo: string;
  eligible: boolean;
  /** eligible=false 일 때 사유(이미 발급됨·대상 결제수단 아님 등). */
  reason: string | null;
  requesterName: string;
  goodsName: string;
  phone: string;
  email: string;
  amount: number;
  taxType: CashReceiptTaxType;
  taxFreeAmount: number;
}

export interface ManualIssueInput {
  orderNo: string | null;
  requesterName: string;
  goodsName: string;
  phone: string;
  email: string;
  taxType: CashReceiptTaxType;
  amount: number;
  supplyAmount: number;
  vatAmount: number;
  taxFreeAmount: number;
  receiptType: 'INCOME_DEDUCTION' | 'EXPENSE_PROOF';
  identityType: 'PHONE' | 'BUSINESS_NO';
  identityNumber: string;
  adminMemo: string;
}

export interface BulkIssueResult {
  issued: number;
  skipped: number;
  failed: Array<{ orderNo: string; reason: string }>;
}

// ── 정규화 ───────────────────────────────────────────────────────────

type Rec = Record<string, unknown>;
const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const sn = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const nn = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const normalizeRow = (r: Rec): CashReceiptRow => ({
  id: s(r.id),
  orderId: sn(r.orderId),
  orderNo: sn(r.orderNo),
  requesterName: s(r.requesterName),
  goodsName: sn(r.goodsName),
  receiptType: s(r.receiptType),
  identityType: s(r.identityType),
  identityMasked: sn(r.identityMasked),
  taxType: s(r.taxType) || 'TAXABLE',
  amount: n(r.amount),
  supplyAmount: n(r.supplyAmount),
  vatAmount: n(r.vatAmount),
  taxFreeAmount: n(r.taxFreeAmount),
  orderAmount: nn(r.orderAmount),
  amountChanged: r.amountChanged === true,
  paymentMethod: sn(r.paymentMethod),
  orderStatus: sn(r.orderStatus),
  issueStatus: s(r.issueStatus),
  requestSource: sn(r.requestSource),
  receiptSource: sn(r.receiptSource),
  issueNumber: sn(r.issueNumber),
  receiptUrl: sn(r.receiptUrl),
  failureMessage: sn(r.failureMessage),
  remainingAmount: n(r.remainingAmount),
  requestedAt: sn(r.requestedAt),
  processedAt: sn(r.processedAt),
});

const normalizeCheck = (r: Rec): OrderCheckResult => ({
  orderId: s(r.orderId),
  orderNo: s(r.orderNo),
  eligible: r.eligible === true,
  reason: sn(r.reason),
  requesterName: s(r.requesterName),
  goodsName: s(r.goodsName),
  phone: s(r.phone),
  email: s(r.email),
  amount: n(r.amount),
  taxType: (['TAXABLE', 'FREE', 'MIXED'] as const).find((t) => t === r.taxType) ?? 'TAXABLE',
  taxFreeAmount: n(r.taxFreeAmount),
});

// ── fetch ────────────────────────────────────────────────────────────

interface Envelope {
  data?: unknown;
  meta?: { pagination?: { totalItems?: number } | null } | null;
}

const readEnvelope = async (res: Response): Promise<Envelope> => {
  if (!res.ok) throw await errorFromResponse(res);
  return (await res.json()) as Envelope;
};

const post = async (path: string, body: unknown): Promise<unknown> => {
  const res = await adminFetch(path, {
    method: 'POST',
    headers: {
      ...(body === null ? {} : { 'Content-Type': 'application/json' }),
      'Idempotency-Key': idempotencyKeyFor('POST', path, body),
    },
    ...(body === null ? {} : { body: JSON.stringify(body) }),
  });
  const env = await readEnvelope(res);
  // 재발급→취소→재발급처럼 같은 본문을 다시 보낼 때 첫 응답 재생으로 묻히지 않게 키를 푼다.
  releaseIdempotencyKey('POST', path, body);
  return env.data;
};

/** 검색 조건 → 쿼리스트링. 기본값(ALL·빈 값)은 보내지 않는다. orderStatuses 는 반복 파라미터. */
export const buildCashReceiptQuery = (q: CashReceiptSearch, page: number, size: number, sort: CashReceiptSort): string => {
  const qs = new URLSearchParams({ page: String(page), size: String(size), sort });
  if (q.keyword.trim()) {
    qs.set('keywordType', q.keywordType);
    qs.set('keyword', q.keyword.trim());
  }
  qs.set('dateType', q.dateType);
  if (q.from) qs.set('from', q.from);
  if (q.to) qs.set('to', q.to);
  q.orderStatuses.forEach((st) => qs.append('orderStatuses', st));
  if (q.issueStatus !== 'ALL') qs.set('issueStatus', q.issueStatus);
  if (q.receiptType !== 'ALL') qs.set('receiptType', q.receiptType);
  if (q.identityType !== 'ALL') qs.set('identityType', q.identityType);
  if (q.taxType !== 'ALL') qs.set('taxType', q.taxType);
  if (q.amountChangedOnly) qs.set('amountChangedOnly', 'true');
  if (q.minAmount.trim()) qs.set('minAmount', q.minAmount.trim());
  if (q.maxAmount.trim()) qs.set('maxAmount', q.maxAmount.trim());
  return qs.toString();
};

export const fetchCashReceipts = async (
  q: CashReceiptSearch,
  page: number,
  size: number,
  sort: CashReceiptSort,
): Promise<CashReceiptPage> => {
  const env = await readEnvelope(await adminFetch(`${BASE}?${buildCashReceiptQuery(q, page, size, sort)}`));
  const d = (env.data ?? {}) as Rec;
  const items = ((d.items as Rec[] | undefined) ?? []).map(normalizeRow);
  return { items, total: env.meta?.pagination?.totalItems ?? items.length };
};

/** 엑셀용 — 검색 결과 전체를 500건씩 모은다. */
export const fetchAllCashReceipts = async (q: CashReceiptSearch, sort: CashReceiptSort): Promise<CashReceiptRow[]> => {
  const SIZE = 500;
  const first = await fetchCashReceipts(q, 0, SIZE, sort);
  const all = [...first.items];
  const pages = Math.ceil(first.total / SIZE);
  for (let p = 1; p < pages; p++) all.push(...(await fetchCashReceipts(q, p, SIZE, sort)).items);
  return all;
};

const listKeyRoot = ['admin', 'cash-receipts'] as const;

export const useCashReceiptsQuery = (q: CashReceiptSearch, page: number, size: number, sort: CashReceiptSort) =>
  useQuery<CashReceiptPage, ApiClientError>({
    queryKey: [...listKeyRoot, q, page, size, sort],
    queryFn: () => fetchCashReceipts(q, page - 1, size, sort),
    placeholderData: keepPreviousData,
    // 404(BE 배포 전)·403 은 다시 시도해도 같다.
    retry: false,
  });

/** 일괄발급 대상 — 실패·취소된 주문 신청(저장된 신청으로 재발급). 입금대기는 입금확인 시 자동 발급, 발급중·발급완료는 제외. */
export const isIssuable = (r: CashReceiptRow): boolean =>
  r.orderId !== null && (r.issueStatus === 'FAILED' || r.issueStatus === 'CANCELED');

/** 취소 대상 — 쇼핑몰(API)·개별발급으로 발급 완료되고 잔액이 남은 건. 고도몰 이관·결제창 발급분은 PG 관리자에서. */
export const isCancellable = (r: CashReceiptRow): boolean =>
  r.issueStatus === 'ISSUED' && r.remainingAmount > 0 && (r.receiptSource === 'API' || r.requestSource === 'MANUAL');

export const useCashReceiptMutations = () => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: listKeyRoot });

  /** 선택 행 일괄발급 — 기존 주문 단위 발급(저장된 신청으로 재발급)을 행마다 부른다. */
  const bulkIssue = useMutation<BulkIssueResult, ApiClientError, CashReceiptRow[]>({
    mutationFn: async (rows) => {
      const result: BulkIssueResult = { issued: 0, skipped: 0, failed: [] };
      for (const r of rows) {
        if (!isIssuable(r)) {
          result.skipped += 1;
          continue;
        }
        try {
          await post(orderPath(r.orderId as string), null);
          result.issued += 1;
        } catch (e) {
          result.failed.push({ orderNo: r.orderNo ?? r.id, reason: friendlyMessage(e, '') });
        }
      }
      return result;
    },
    onSettled: refresh,
  });

  const cancel = useMutation<unknown, ApiClientError, { row: CashReceiptRow; reason: string }>({
    mutationFn: ({ row, reason }) =>
      row.orderId
        ? post(`${orderPath(row.orderId)}/cancel`, { reason: reason || undefined })
        : post(`${BASE}/${encodeURIComponent(row.id)}/cancel`, { reason: reason || undefined }),
    onSettled: refresh,
  });

  const manualIssue = useMutation<unknown, ApiClientError, ManualIssueInput>({
    mutationFn: (input) => post(BASE, input),
    onSettled: refresh,
  });

  return { bulkIssue, cancel, manualIssue };
};

export const checkOrderForCashReceipt = async (orderNo: string): Promise<OrderCheckResult> => {
  const env = await readEnvelope(
    await adminFetch(`${BASE}/order-check?orderNo=${encodeURIComponent(orderNo.trim())}`, { cache: 'no-store' }),
  );
  return normalizeCheck((env.data ?? {}) as Rec);
};
